import {
  Axis,
  Dimension,
  Wrap,
  Justify,
  JUSTIFY_SPACING_SIZE_MODIFIERS,
  JUSTIFY_SPACING_MODIFIER,
  AlignItems,
  Side,
} from './constants'

const adjustItemInCenter = (item, axis, freeSpace) => item.adjustPosition(axis, freeSpace / 2)

const adjustItemToEnd = (item, axis, freeSpace) => item.adjustPosition(axis, freeSpace)

const applyAlignItems = (items, align, axis, lineSize) => {
  const dimension = axis === Axis.X ? Dimension.WIDTH : Dimension.HEIGHT

  items.forEach((item) => {
    const freeSpace = lineSize - item.getOuterSize(dimension)
    switch (align) {
      case AlignItems.CENTER:
        return adjustItemInCenter(item, axis, freeSpace)
      case AlignItems.FLEX_END:
        return adjustItemToEnd(item, axis, freeSpace)
    }
  })
}

const applyJustifyContent = (items, justify, axis, freeSpace) => {
  switch (justify) {
    case Justify.CENTER:
      items.forEach((item) => adjustItemInCenter(item, axis, freeSpace))
      break
    case Justify.FLEX_END:
      items.forEach((item) => adjustItemToEnd(item, axis, freeSpace))
      break
    case Justify.SPACE_BETWEEN:
    case Justify.SPACE_AROUND:
    case Justify.SPACE_EVENLY:
      {
        const spacingSizeModifier = JUSTIFY_SPACING_SIZE_MODIFIERS[justify]
        const spacingModifier = JUSTIFY_SPACING_MODIFIER[justify]

        const spacing = freeSpace / Math.max(items.length + spacingSizeModifier, 1)
        for (let i = 0; i < items.length; i++) {
          items[i].adjustPosition(axis, spacing * (i + 1) - spacing * spacingModifier)
        }
      }
      break
  }
}

export default function applyFlex(
  baseItem,
  flexItems,
  {
    dimension,
    flexWrap = Wrap.NOWRAP,
    justifyContent = Justify.FLEX_START,
    alignItems = AlignItems.STRETCH,
  },
  layout,
) {
  const crossDimension = dimension === Dimension.WIDTH ? Dimension.HEIGHT : Dimension.WIDTH
  const axis = dimension === Dimension.WIDTH ? Axis.X : Axis.Y
  const crossAxis = dimension === Dimension.WIDTH ? Axis.Y : Axis.X

  const axisSize = baseItem.getGivenInnerSize(baseItem.flexDimension)
  const crossAxisSize = baseItem.getGivenInnerSize(baseItem.flexCrossDimension)
  const useCrossAxisSize = crossAxisSize != null
  const axisOffset = baseItem.getInnerOffset(baseItem.isFlexColumn ? Side.TOP : Side.LEFT)
  const crossAxisOffset = baseItem.getInnerOffset(baseItem.isFlexColumn ? Side.LEFT : Side.TOP)

  const lines = []
  const createNewLine = () => ({
    items: [],
    realAxisSize: 0,
    realCrossAxisSize: 0,
    finalSize: 0,
    finalCrossSize: 0,
    crossPosition: 0,
  })
  let currentLine = createNewLine()
  for (let i = 0; i < flexItems.length; i++) {
    const item = flexItems[i]
    currentLine.items.push(item)
    currentLine.realAxisSize += item.getOuterSize(dimension)
    currentLine.realCrossAxisSize = Math.max(
      currentLine.realCrossAxisSize,
      item.getOuterSize(crossDimension),
    )

    if (flexWrap === Wrap.NOWRAP || axisSize == null) {
      continue
    }

    const nextItem = flexItems[i + 1]
    if (nextItem && currentLine.realAxisSize + nextItem.getOuterSize(dimension) > axisSize) {
      lines.push(currentLine)
      currentLine = createNewLine()
    }
  }

  if (currentLine.items.length > 0) {
    lines.push(currentLine)
  }

  const linesRealCrossSize = lines.reduce((sum, e) => sum + e.realCrossAxisSize, 0)
  const crossAxisFreeSpace = useCrossAxisSize
    ? Math.max(crossAxisSize - linesRealCrossSize, 0) / lines.length
    : 0

  let crossPosition = crossAxisOffset
  let finalSize = 0
  let finalCrossSize = 0
  for (const line of lines) {
    const { items, realAxisSize } = line
    const axisSizeDiff = axisSize != null ? realAxisSize - axisSize : 0
    const hasFreeSpace = axisSizeDiff < 0
    const isGrow = hasFreeSpace
    let restSizeDiff = Math.abs(axisSizeDiff)

    // Flex shrink & grow
    if (axisSize != null) {
      const getValue = isGrow ? getFlexGrow : getFlexShrink

      const resizableItems = items.filter((item) => getValue(item) !== 0)
      const maxIterations = resizableItems.length
      for (let i = 0; i < maxIterations; i++) {
        const valueSum = resizableItems.reduce((sum, item) => sum + getValue(item), 0)
        const changeBasis = restSizeDiff / valueSum
        restSizeDiff = 0

        for (let j = resizableItems.length - 1; j >= 0; j--) {
          const item = resizableItems[j]
          const baseAmount = changeBasis * getValue(item)
          const plannedChangeAmount = isGrow ? baseAmount : -baseAmount
          const factChangeAmount = item.adjustFlexSize(plannedChangeAmount)
          const diff = Math.abs(plannedChangeAmount - factChangeAmount)
          if (diff > 0) {
            restSizeDiff += diff
            resizableItems.splice(j, 1)
          }
        }

        // This check comes in the end of iteration cause we have to call adjustFlexSize
        // on all resizable items ones even if restSizeDiff is 0 to apply flexSize
        if (restSizeDiff === 0 || resizableItems.length === 0) {
          break
        }
      }
    }

    // Cross axis resize
    const maxCrossSize = crossAxisSize
    const stretchCrossSize = Math.min(line.realCrossAxisSize + crossAxisFreeSpace, maxCrossSize)

    let currentPosition = axisOffset
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const { flexSize } = item
      let relayout = flexSize != null

      if (useCrossAxisSize) {
        if (alignItems === AlignItems.STRETCH) {
          if (item.flexCrossSize !== stretchCrossSize) {
            item.flexCrossSize = stretchCrossSize
            relayout = true
          }
        } else {
          const realCrossSize = item.getOuterSize(crossDimension)
          if (realCrossSize > maxCrossSize) {
            item.flexCrossSize = maxCrossSize
            relayout = true
          }
        }
      }

      if (relayout) {
        layout(item)
      }
      item.setOuterPosition(axis, currentPosition)
      item.setOuterPosition(crossAxis, crossPosition)
      const itemSize = item.getOuterSize(dimension)
      const itemCrossSize = item.getOuterSize(crossDimension)
      currentPosition += itemSize
      line.finalSize += itemSize
      line.finalCrossSize = Math.max(
        line.finalCrossSize,
        itemCrossSize + (alignItems === AlignItems.STRETCH ? 0 : crossAxisFreeSpace),
      )
    }

    if (hasFreeSpace && restSizeDiff > 0) {
      applyJustifyContent(line.items, justifyContent, axis, restSizeDiff)
    }

    applyAlignItems(line.items, alignItems, crossAxis, line.finalCrossSize)

    crossPosition += line.finalCrossSize
    finalCrossSize += line.finalCrossSize
    if (line.finalSize > finalSize) {
      finalSize = line.finalSize
    }
  }

  if (dimension === Dimension.WIDTH) {
    return { width: finalSize, height: finalCrossSize }
  }
  return { width: finalCrossSize, height: finalSize }
}

function getFlexGrow(item) {
  return item.flexGrow
}

function getFlexShrink(item) {
  return item.flexShrink
}
